import React from 'react';

const classificationStyles = {
  UNCLASSIFIED: 'bg-green-100 text-green-800 border-green-200',
  PUBLIC: 'bg-green-100 text-green-800 border-green-200',
  INTERNAL: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  RESTRICTED: 'bg-red-100 text-red-800 border-red-200',
  CONFIDENTIAL: 'bg-white text-gray-900 border-gray-400',
  SECRET: 'bg-white text-gray-900 border-gray-400',
  'TOP SECRET': 'bg-white text-gray-900 border-gray-400',
  UNKNOWN: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Function to clean caveats and releasability arrays
function cleanClassificationComponents(caveats = [], releasability = []) {
  // Valid caveats (short codes only)
  const validCaveats = ['SI', 'HCS', 'TK', 'GAMMA', 'ORCON', 'FOUO', 'LIMDIS'];
  
  // Clean caveats - only keep recognized short codes, filter out verbose explanations
  const cleanCaveats = caveats.filter(caveat => {
    if (!caveat || typeof caveat !== 'string') return false;
    const upperCaveat = caveat.toUpperCase().trim();
    // Only include if it's a recognized caveat code (not verbose text)
    return validCaveats.includes(upperCaveat);
  });
  
  // Clean releasability - only keep entries that look like proper releasability markings
  const cleanReleasability = releasability.filter(rel => {
    if (!rel || typeof rel !== 'string') return false;
    const upperRel = rel.toUpperCase().trim();
    // Keep if it starts with known releasability patterns or is NOFORN
    return upperRel === 'NOFORN' || 
           upperRel.startsWith('REL TO') || 
           upperRel.startsWith('RELTO') ||
           upperRel === 'NF';
  });
  
  return { cleanCaveats, cleanReleasability };
}

// Function to clean and extract only the classification level from garbled text
function extractCleanClassification(fullClassification, baseClassification, caveats = [], releasability = []) {
  // Clean the component arrays first
  const { cleanCaveats, cleanReleasability } = cleanClassificationComponents(caveats, releasability);
  
  // ALWAYS prioritize building from components if they are provided
  if (cleanCaveats.length > 0 || cleanReleasability.length > 0) {
    const parts = [baseClassification.toUpperCase()];
    if (cleanCaveats.length > 0) {
      parts.push(...cleanCaveats);
    }
    if (cleanReleasability.length > 0) {
      parts.push(...cleanReleasability);
    }
    return parts.join('//');
  }
  
  if (!fullClassification || fullClassification === baseClassification) {
    // Build from components if no full classification or if it's the same as base
    const parts = [baseClassification.toUpperCase()];
    if (caveats && caveats.length > 0) {
      parts.push(...caveats);
    }
    if (releasability && releasability.length > 0) {
      parts.push(...releasability);
    }
    return parts.join('//');
  }
  
  // Try to extract clean classification from potentially garbled text
  let cleaned = fullClassification.toUpperCase();
  
  // Look for the main classification levels
  const classificationLevels = ['TOP SECRET', 'SECRET', 'CONFIDENTIAL', 'UNCLASSIFIED'];
  let baseLevel = 'UNKNOWN';
  
  for (const level of classificationLevels) {
    if (cleaned.includes(level)) {
      baseLevel = level;
      break;
    }
  }
  
  // Extract clean parts by looking for standard classification components
  const parts = [baseLevel];
  
  // Look for compartments
  const compartments = ['SI', 'HCS', 'TK', 'GAMMA', 'ORCON'];
  for (const comp of compartments) {
    if (cleaned.includes('//' + comp + '//') || cleaned.includes(' ' + comp + ' ') || cleaned.includes('//' + comp)) {
      if (!parts.includes(comp)) {
        parts.push(comp);
      }
    }
  }
  
  // Look for releasability markings
  const releasabilityMarkings = ['NOFORN', 'ORCON', 'FOUO', 'LIMDIS'];
  for (const rel of releasabilityMarkings) {
    if (cleaned.includes(rel)) {
      if (!parts.includes(rel)) {
        parts.push(rel);
      }
    }
  }
  
  return parts.join('//');
}

export default function ClassificationBadge({ 
  classification, 
  fullClassification, 
  caveats = [], 
  releasability = [], 
  confidence, 
  className = '' 
}) {
  // Clean and extract proper classification
  const displayText = extractCleanClassification(fullClassification, classification, caveats, releasability);
  
  // Get base level for styling (first part before //)
  const baseLevel = displayText.split('//')[0];
  const style = classificationStyles[baseLevel] || classificationStyles.UNKNOWN;

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${style} ${className}`}>
      <span className="font-mono">{displayText}</span>
      {confidence && (
        <span className="ml-2 text-xs opacity-75">
          ({Math.round(confidence * 100)}%)
        </span>
      )}
    </div>
  );
}